import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCompleteJobInformation, useInvalidateJobQueries, jobKeys } from './useJobOffers';
import {
  Candidate,
  JobOffer,
  Skill,
  MatchingPriorities,
  DEFAULT_MATCHING_PRIORITIES,
} from '../models/job';
import { useTrackScoringCompletion } from './useTrackScoringCompletion';
import { BLOCKING_FIELDS, isSelectField, isNumberField } from '../components/dashboard/create-job-modal/fieldConfig';
import { getFieldValue, RECOMMENDED_FIELDS } from '../utils/missingFieldsDetection';
import i18n from '@/i18n';

interface UseCompleteJobInfoFormOptions {
  jobId: string;
  missingFields: string[];
  criticalMissingFields: string[];
  isOpen: boolean;
  job: JobOffer | null;
}

export type WorkflowPhase = 'form' | 'loading' | 'complete';

interface UseCompleteJobInfoFormResult {
  formData: Record<string, any>;
  tagInputValues: Record<string, string>;
  phase: WorkflowPhase;
  isSubmitting: boolean;
  formFields: string[];
  step: number;
  steps: { key: string; label: string }[];
  updateFormData: (fieldName: string, value: string | Skill[] | MatchingPriorities) => void;
  updateTagInput: (fieldName: string, value: string) => void;
  handleSubmit: () => void;
  handleClose: () => boolean;
  resetForm: () => void;
  handleNextStep: () => void;
  handlePrevStep: () => void;
  hasCandidates: boolean;
}

export const useCompleteJobInfoForm = ({
  jobId,
  missingFields,
  criticalMissingFields,
  isOpen,
  job,
}: UseCompleteJobInfoFormOptions): UseCompleteJobInfoFormResult => {
  const { t } = useTranslation('dashboard');
  console.debug('[useCompleteJobInfoForm] missingFields:', missingFields);
  const queryClient = useQueryClient();
  const invalidateJobQueries = useInvalidateJobQueries();
  const completeJobInfoMutation = useCompleteJobInformation(jobId);
  const dismissedStorageKey = `job-refine-dismissed-${jobId}`;

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [tagInputValues, setTagInputValues] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<WorkflowPhase>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formFields, setFormFields] = useState<string[]>([]);

  const updateFormData = useCallback((fieldName: string, value: string | Skill[] | MatchingPriorities) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  }, []);

  const updateTagInput = useCallback((fieldName: string, value: string) => {
    setTagInputValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  }, []);


  const [step, setStep] = useState(0);

  const steps = useMemo(
    () => [
      { key: 'intro', label: t('create_job_modal.step_intro') },
      { key: 'details', label: t('create_job_modal.section_job_details') },
      { key: 'skills', label: t('create_job_modal.section_skills') },
      { key: 'company', label: t('create_job_modal.section_company') },
      { key: 'weighting', label: t('create_job_modal.section_weighting') },
    ],
    [t]
  );

  // Fields that are always required regardless of criticalMissingFields
  const ALWAYS_REQUIRED_FIELDS = ['Job Title', 'Years of Experience', 'Required Degree'];

  const validateStep = useCallback((stepIndex: number): boolean => {
    if (stepIndex === 0) return true; // Intro step is always valid

    const fieldsToValidate = (() => {
      switch (stepIndex) {
        case 1: // Job Details
          return ['Job Title', 'Location', 'Contract Type', 'Remote Policy', 'Years of Experience', 'Required Degree', 'Role Description'];
        case 2: // Skills (Validation handled by UI, skills are optional but must be valid if present)
          return ['Coding Languages', 'Frameworks & Libraries'];
        case 3: // Company
          return ['Company Name', 'Company Sector', 'Company Description'];
        case 4: // Weighting
          return ['Matching Priorities'];
        default:
          return [];
      }
    })();

    const isFieldRequired = (field: string): boolean =>
      criticalMissingFields.includes(field) || ALWAYS_REQUIRED_FIELDS.includes(field);

    const stepValid = fieldsToValidate.every(field => {
      if (isFieldRequired(field)) {
          const value = formData[field];
          if (Array.isArray(value)) return value.length > 0;
          if (isNumberField(field)) return value !== undefined && value !== null && value !== '';
          if (isSelectField(field)) return typeof value === 'string' && value.length > 0;
          return typeof value === 'string' && value.trim().length > 0;
      }
      // Check if we require at least one skill for "Coding Languages" or "Frameworks & Libraries"
      if (field === 'Coding Languages' || field === 'Frameworks & Libraries') {
          return true;
      }
      return true;
    });

    // Special validation for Skills step (Step 2)
    if (stepIndex === 2) {
        const skills = formData['Skills'] || [];
        if (!Array.isArray(skills) || skills.length === 0) {
             toast.error(i18n.t('job:toasts.complete_job_add_skill'));
             return false;
        }
    }

    if (!stepValid) {
        toast.error(i18n.t('common:toasts.fill_required_fields_marked'));
        return false;
    }

    return true;
  }, [formData, criticalMissingFields, t]);

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrevStep = () => {
    setStep(prev => Math.max(prev - 1, 0));
  };


  // Check if there are pending candidates that need rescoring
  const hasPendingCandidates = useCallback((): boolean => {
    const candidates = queryClient.getQueryData<Candidate[]>(
      jobKeys.candidatesForJob(jobId)
    );
    if (!candidates || candidates.length === 0) return false;
    return candidates.some((c) => c.pipelineStageSemantic === 'pending');
  }, [jobId, queryClient]);

  const [scoringDone, setScoringDone] = useState(false);

  useTrackScoringCompletion({
    jobId,
    enabled: phase === 'loading' && isOpen && hasPendingCandidates(),
    onComplete: () => {
      setScoringDone(true);
    },
  });

  // Poll job detail while loading to detect requires_review flip
  useEffect(() => {
    if (phase !== 'loading' || !isOpen) return;

    const interval = setInterval(() => {
      queryClient.refetchQueries({ queryKey: jobKeys.detail(jobId) });
    }, 2000);

    return () => clearInterval(interval);
  }, [phase, isOpen, jobId, queryClient]);

  // Transition to complete when backend is done:
  // - requires_review must be false (backend finished)
  // - if there were candidates, scoring must be done too
  useEffect(() => {
    if (phase !== 'loading' || !isOpen) return;
    if (job?.requiresReview !== false) return;

    const pending = hasPendingCandidates();
    if (!pending || scoringDone) {
      setPhase('complete');
    }
  }, [phase, isOpen, job?.requiresReview, hasPendingCandidates, scoringDone]);

  const handleSubmit = useCallback(() => {
    if (!validateStep(step)) {
      return;
    }

    if (isSubmitting || completeJobInfoMutation.isPending) {
      return;
    }

    console.debug('[useCompleteJobInfoForm] submitting formData keys:', Object.keys(formData));

    setIsSubmitting(true);

    completeJobInfoMutation.mutate(formData, {
      onSuccess: () => {
        if (jobId && typeof window !== 'undefined') {
          // Start dashboard/job-card processing lock immediately when exam config generation starts.
          window.sessionStorage.setItem(dismissedStorageKey, '1');
        }
        setPhase('loading');
        invalidateJobQueries(jobId, { shouldPoll: false });
      },
      onError: (error: Error) => {
        setIsSubmitting(false);
        setPhase('form');
        toast.error(i18n.t('job:toasts.complete_job_update_failed', { error: error.message }));
      },
    });
  }, [
    formData,
    validateStep,
    step,
    isSubmitting,
    completeJobInfoMutation,
    invalidateJobQueries,
    jobId,
  ]);

  const resetForm = useCallback(() => {
    setFormData({});
    setTagInputValues({});
    setScoringDone(false);
  }, []);

  const handleClose = useCallback((): boolean => {
    const wasCompleted = phase === 'complete';
    setPhase('form');
    setIsSubmitting(false);
    resetForm();
    return wasCompleted;
  }, [phase, resetForm]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function normalizeSkillValue(value: unknown): Skill[] {
      if (!Array.isArray(value)) {
        return [];
      }

      return value
        .map((entry) => {
          if (typeof entry === 'string') {
            return { name: entry, importance: 'uncertain' as const };
          }

          if (entry && typeof entry === 'object' && 'name' in entry) {
            return entry as Skill;
          }

          return null;
        })
        .filter((skill): skill is Skill => Boolean(skill?.name));
    }

    const initialData: Record<string, any> = {};

    [...BLOCKING_FIELDS, ...RECOMMENDED_FIELDS].forEach((field) => {
      const jobValue = job ? getFieldValue(job, field) : undefined;

      if (field === 'Skills') {
        const currentSkills =
          (Array.isArray(job?.skills) && job.skills.length > 0)
            ? job.skills
            : [...(job?.codingLanguages || []), ...(job?.frameworksLibraries || [])];
        initialData[field] = normalizeSkillValue(currentSkills);
        return;
      }

      if (jobValue === null || jobValue === undefined) {
        initialData[field] = '';
        return;
      }

      if (typeof jobValue === 'number') {
        initialData[field] = jobValue.toString();
        return;
      }

      initialData[field] = String(jobValue);
    });

    // Normalize mission role field so the UI label "Role Description" is prefilled
    if (job?.mission_role_in_job) {
      initialData['Role Description'] = job.mission_role_in_job;
    }

    initialData['Job Title'] = job?.title ?? '';
    initialData['Matching Priorities'] = job?.matchingPriorities ?? { ...DEFAULT_MATCHING_PRIORITIES };

    setFormData(initialData);
    console.debug('[useCompleteJobInfoForm] initialized formData keys:', Object.keys(initialData));
    setFormFields(['Job Title', ...BLOCKING_FIELDS, ...RECOMMENDED_FIELDS]);
    setTagInputValues({});
    if (phase === 'form') {
      setIsSubmitting(false);
    }
  }, [isOpen, job, phase]);

  return {
    formData,
    tagInputValues,
    phase,
    isSubmitting,
    formFields,
    step,
    steps,
    updateFormData,
    updateTagInput,
    handleSubmit,
    handleClose,
    resetForm,
    handleNextStep,
    handlePrevStep,
    hasCandidates: hasPendingCandidates(),
  };
};
